import { useQuery, useInfiniteQuery } from '@tanstack/react-query';

import { useWalletAddress } from '@/hooks/useWalletAddress';
import { getPointsBalance, getPointsLedger } from '@/api/points';
import type { PointsLedgerResponse } from '@/types/schemas';

export function usePointsBalanceSnapshot(enabled = true) {
  const { address } = useWalletAddress();
  const walletAddress = address ?? undefined;

  return useQuery({
    queryKey: ['points', 'balance', walletAddress ?? 'anon'],
    queryFn: () => getPointsBalance({ wallet_address: walletAddress }),
    enabled: enabled && !!address,
    staleTime: 60_000, // 1 minute
    gcTime: 5 * 60_000, // 5 minutes
    refetchInterval: 60_000, // Reduced from 15s to 60s to reduce API load
    refetchOnWindowFocus: false, // Disable to reduce API calls
  });
}

export function usePointsLedger({
  page = 1,
  limit = 25,
  enabled = true,
}: {
  page?: number;
  limit?: number;
  enabled?: boolean;
}) {
  const { address } = useWalletAddress();
  const walletAddress = address ?? undefined;

  return useQuery<PointsLedgerResponse>({
    queryKey: ['points', 'ledger', walletAddress ?? 'anon', page, limit],
    queryFn: async () => {
      try {
        const result = await getPointsLedger({
          wallet_address: walletAddress,
          page,
          limit,
        });
        return result;
      } catch (error) {
        console.error('[usePointsLedger] Error:', error);
        throw error;
      }
    },
    enabled: enabled && !!walletAddress,
    placeholderData: (previousData) => previousData,
    initialData: undefined, // Don't use initial data to avoid flash
    staleTime: 30 * 1000, // 30 seconds - allow more frequent updates
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: true, // Refetch on window focus to get latest data
    refetchOnMount: true, // Always fetch fresh data on mount
    retry: 2, // Retry twice on failure
    retryDelay: 1000, // Wait 1 second before retry
    networkMode: 'online', // Only fetch when online
  });
}

/**
 * Infinite scroll hook for points ledger with pagination
 * Loads 10 entries at a time with auto-loading on scroll
 */
export function usePointsLedgerInfinite({
  limit = 10,
  enabled = true,
}: {
  limit?: number;
  enabled?: boolean;
} = {}) {
  const { address } = useWalletAddress();
  const walletAddress = address ?? undefined;

  return useInfiniteQuery<PointsLedgerResponse>({
    queryKey: ['points', 'ledger', 'infinite', walletAddress ?? 'anon', limit],
    queryFn: async ({ pageParam = 1 }) => {
      const result = await getPointsLedger({
        wallet_address: walletAddress,
        page: pageParam as number,
        limit,
      });

      return result;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      // If there's more data, return the next page number
      if (lastPage.hasMore) {
        return allPages.length + 1;
      }
      // Otherwise, return undefined to indicate no more pages
      return undefined;
    },
    enabled: enabled && !!walletAddress,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false, // Don't refetch on window focus for infinite scroll
    retry: 2,
    retryDelay: 1000,
    networkMode: 'online',
  });
}
